import React, { useState, useEffect } from "react";
import FormSubmitButton from "../../../Components/layouts/formSubmitButton";
import SimpleButton from "../../../Components/layouts/simple_button";
import Footer from "../../../Components/Footer/footer";
import { Formik, Form, ErrorMessage } from "formik";
import CustomInput from "../../../CustomComponent/custom_input";
import performRequest from "../../../api/request";
import { EmployeeLeavesInfo } from "../../../SchemaInfo/info";
import { EmployeeLeaveNewSchema } from "../../../Schemas/employee/employee_leave_schema";
import CustomTextArea from "../../../CustomComponent/custom_textarea";
import CustomRadioButton from "../../../CustomComponent/custom_radio";
import { useDispatch, useSelector } from "react-redux";
import { Toast } from "../../../CustomComponent/toast";
import { BsCalendar2XFill } from "react-icons/bs";
import { FcCalendar } from "react-icons/fc";
import { useNavigate } from "react-router";
import { CustomErrorResponse } from "../../../CustomComponent/custom_error_response";
import { finalOfficeLogout } from "../../../redux/user/loginReducer";

const getLeavesTaken = (obj) => {
  if (obj.leaveType === "half day") {
    return 0.5;
  }
  if (obj.leaveType === "full day") {
    return 1;
  } else {
    const start = new Date(obj.leaveStartDate);
    const end = new Date(obj.leaveEndDate);
    return (end - start) / (1000 * 60 * 60 * 24) + 1;
  }
};

const checkLeaveOverlap = (leavesDetails, values) => {
  const start = new Date(values.leaveStartDate || values.leaveDate);
  const end = new Date(values.leaveEndDate || values.leaveDate);

  return leavesDetails.some((leave) => {
    const leaveStart = new Date(leave.leaveStartDate || leave.leaveDate);
    const leaveEnd = new Date(leave.leaveEndDate || leave.leaveDate);
    const leaveEndStatus = leave.leaveStatus;
    return (
      (start >= leaveStart &&
        start <= leaveEnd &&
        leaveEndStatus != "rejected") ||
      (end >= leaveStart && end <= leaveEnd && leaveEndStatus != "rejected") ||
      (start <= leaveStart && end >= leaveEnd && leaveEndStatus != "rejected")
    );
  });
};

const checkUserBefoureNine = (val) => {
  let halfDayType = val.halfDayType;
  let leaveDate = val.leaveDate;
  let leaveType = val.leaveType;
  let today = new Date().toISOString().split("T")[0];

  let now = new Date();
  let currentMinutes = now.getHours() * 60 + now.getMinutes();

  const LIMIT_FULL_HALF = 9 * 60;
  const LIMIT_SECOND_HALF = 13 * 60 + 30;
  if (leaveDate === today) {
    if (leaveType === "half day" && halfDayType === "second half") {
      if (currentMinutes > LIMIT_SECOND_HALF) {
        return "❌ Cannot apply second-half leave after 1:30 PM.";
      }
    } else {
      if (currentMinutes > LIMIT_FULL_HALF) {
        return "❌ Leave request not allowed after 9:00 AM.";
      }
    }
  }
};

const getQuarter = (date) => {
  const month = date.getMonth() + 1;
  return Math.ceil(month / 3);
};

const AddEmployeeLeves = () => {
  const employee = useSelector((store) => store.login.user);
  const [leaveCount, setLeaveCount] = useState({ paid: 0, unpaid: 0 });
  const [quarterlyLeaveCount, setQuarterlyLeaveCount] = useState({});
  const [userLeaveData, setUserLeaveData] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const patchId = employee.UserId;
  const [disableButton, setDisableButton] = useState(false);
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const fetchLeaveData = async (empId) => {
    try {
      const res = await performRequest("GET", `/leave/getleavesById/${empId}`);
      if (!res?.data?.error) {
        setUserLeaveData(res.data);
        const paidLeaves = res.data.leavesDetails
          .filter((leave) => leave.leaveReasonType === "sick")
          .reduce((total, leave) => total + getLeavesTaken(leave), 0);
        const unpaidLeaves = res.data.leavesDetails
          .filter((leave) => leave.leaveReasonType === "casual")
          .reduce((total, leave) => total + getLeavesTaken(leave), 0);

        setLeaveCount({ paid: paidLeaves, unpaid: unpaidLeaves });

        const quarterlyCount = res.data.leavesDetails.reduce((acc, leave) => {
          const quarter = getQuarter(
            new Date(leave.leaveDate || leave.leaveStartDate)
          );
          acc[quarter] = (acc[quarter] || 0) + getLeavesTaken(leave);
          return acc;
        }, {});
        setQuarterlyLeaveCount(quarterlyCount);
      }
    } catch (err) {
      // console.error(err);
      CustomErrorResponse(err);
    }
  };

  const fetchEmployeeDetails = async (empId) => {
    try {
      const res = await performRequest("GET", `/login/getUserById/${empId}`);
      setEmployeeDetails(res.data);
    } catch (err) {
      CustomErrorResponse(err);
    }
  };

  useEffect(() => {
    if (employee.UserId) {
      fetchLeaveData(employee.UserId);
      fetchEmployeeDetails(employee.UserId);
    }
  }, [employee.UserId]);

  const handleSubmit = async (values, actions) => {
    setDisableButton(true);
    if (
      userLeaveData &&
      checkLeaveOverlap(userLeaveData.leavesDetails, values)
    ) {
      actions.setFieldError(
        "leaveDate",
        "You have already taken a leave on this day."
      );
      setDisableButton(false);
      return;
    }

    let checkTime = checkUserBefoureNine(values);
    if (checkTime) {
      Toast("error", checkTime);
      setDisableButton(false);
      return;
    }

    const takenLeaves = getLeavesTaken(values);
    const leaveQuarter = getQuarter(
      new Date(values.leaveDate || values.leaveStartDate)
    );
    const currentQuarterLeaves =
      (quarterlyLeaveCount[leaveQuarter] || 0) + takenLeaves;

    if (
      currentQuarterLeaves > 4 &&
      employeeDetails?.employeementStatus === "activePosition"
    ) {
      Toast("error", "You have exceeded the quarterly leave limit.");
    }

    let updatedLeaveCount = { ...leaveCount };
    let updatedQuarterlyLeaveCount = {
      ...quarterlyLeaveCount,
      [leaveQuarter]: currentQuarterLeaves,
    };

    if (values.leaveReasonType === "sick") {
      updatedLeaveCount.paid += takenLeaves;
    } else if (values.leaveReasonType === "casual") {
      updatedLeaveCount.unpaid += takenLeaves;
    }

    try {
      const apiUrl = userLeaveData?.id
        ? `/leave/updateleavesNew/${patchId}`
        : `/leave/createleaves/`;
      const method = userLeaveData?.id ? "PATCH" : "POST";
      const successMsg = userLeaveData?.id
        ? "Leave updated successfully!"
        : "Leave created successfully!";

      let supportingDocument = values.supportingDocument || "";
      if (values.supportingDocument instanceof window.File) {
        let formData = new FormData();
        formData.append("file", values.supportingDocument);
        const headers = { "Content-Type": "multipart/form-data" };
        performRequest("POST", `/leave/fileupload/`, formData, headers)
          .then((res) => {
            if (res.status === 201) {
              supportingDocument = res.data.file;
              let leaveData = {
                userId: employee?.UserId,
                employeeName: employee?.Name,
                employeeRole: employee?.Role,
                employeeStatus: employeeDetails?.employeementStatus,
                takenLeaves: updatedLeaveCount.paid + updatedLeaveCount.unpaid,
                totalLeaves:
                  employeeDetails?.employeementStatus === "activePosition"
                    ? 15
                    : 0,
                sickLeaves:
                  employeeDetails?.employeementStatus === "activePosition"
                    ? 8
                    : 0,
                casualLeaves:
                  employeeDetails?.employeementStatus === "activePosition"
                    ? 7
                    : 0,
                pendingLeaves:
                  employeeDetails?.employeementStatus === "activePosition"
                    ? 15 - (updatedLeaveCount.paid + updatedLeaveCount.unpaid)
                    : 0,
                leavesDetails: userLeaveData
                  ? [
                      ...userLeaveData.leavesDetails,
                      { ...values, supportingDocument },
                    ]
                  : [{ ...values, supportingDocument }],
              };

              uploadLeaves(
                method,
                apiUrl,
                leaveData,
                updatedQuarterlyLeaveCount,
                successMsg,
                updatedLeaveCount,
                actions
              );
            } else {
              Toast("error", "Error while uploading file!");
              setDisableButton(false);
            }
          })
          .catch((err) => {
            CustomErrorResponse(err);

            setDisableButton(false);
          });
      } else {
        let leaveData = {
          userId: employee?.UserId,
          employeeName: employee?.Name,
          employeeRole: employee?.Role,
          employeeStatus: employeeDetails?.employeementStatus,
          takenLeaves: updatedLeaveCount.paid + updatedLeaveCount.unpaid,
          totalLeaves:
            employeeDetails?.employeementStatus === "activePosition" ? 15 : 0,
          sickLeaves:
            employeeDetails?.employeementStatus === "activePosition" ? 8 : 0,
          casualLeaves:
            employeeDetails?.employeementStatus === "activePosition" ? 7 : 0,
          pendingLeaves:
            employeeDetails?.employeementStatus === "activePosition"
              ? 15 - (updatedLeaveCount.paid + updatedLeaveCount.unpaid)
              : 0,
          leavesDetails: userLeaveData
            ? [
                ...userLeaveData.leavesDetails,
                { ...values, supportingDocument },
              ]
            : [{ ...values, supportingDocument }],
        };
        uploadLeaves(
          method,
          apiUrl,
          leaveData,
          updatedQuarterlyLeaveCount,
          successMsg,
          updatedLeaveCount,
          actions
        );
      }
    } catch (err) {
      CustomErrorResponse(err);
    }
  };

  const uploadLeaves = async (
    method,
    apiUrl,
    leaveData,
    updatedQuarterlyLeaveCount,
    successMsg,
    updatedLeaveCount,
    actions
  ) => {
    const res = await performRequest(method, apiUrl, leaveData);

    if (res.status === 200 || res.status === 201) {
      setLeaveCount(updatedLeaveCount);
      setQuarterlyLeaveCount(updatedQuarterlyLeaveCount);
      Toast("success", res?.data?.message || successMsg);
      await fetchLeaveData(employee.UserId);

      if (res?.data?.attendanceDeleted) {
        dispatch(finalOfficeLogout());
      } else {
        navigate("/employee-leave-management");
      }
      actions.resetForm();
      setDisableButton(false);
    } else {
      Toast("error", "Error while processing leave.");
      setDisableButton(false);
    }
  };

  return (
    <div className="page">
      <div className="page-wrapper">
        <div className="container-xl">
          <div className="page-header d-print-none">
            <div className="container-xl">
              <div className="row align-items-center">
                <div className="col-md-8">
                  <h2 className="page-title mb-1">
                    <FcCalendar className="fs-2  me-1" /> Apply Leaves
                  </h2>
                  <h4 className="text-muted">
                    {/* <StepRoutes /> */} Apply leaves.
                  </h4>
                </div>
                <div className="col-md-4">
                  <div className="btn-list justify-content-end">
                    <a href={"/employee-leave-management"}>
                      <button className="btn btn-primary">
                        <BsCalendar2XFill className="me-2 fs-2" /> VIEW ALL
                        LEAVES
                      </button>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-4 col-sm-12">
              <div className="card">
                <div class="table-responsive">
                  <table class="table table-vcenter card-table table-striped">
                    <thead>
                      <tr>
                        <th>Occasion</th>
                        <th>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          26th January 2026
                          <br />
                          <span class="text-secondary">Republic Day</span>
                        </td>
                        <td class="text-secondary">Full Day</td>
                      </tr>
                      <tr>
                        <td>
                          19th Feburary 2026
                          <br />
                          <span class="text-secondary">
                            Chhtrapati Shivaji Maharaj Jayanti
                          </span>
                        </td>
                        <td class="text-secondary">Full Day</td>
                      </tr>
                      <tr>
                        <td>
                          3th March 2026
                          <br />
                          <span class="text-secondary">Dhulivandan/ Holi</span>
                        </td>
                        <td class="text-secondary">Full Day</td>
                      </tr>
                      <tr>
                        <td>
                          19th March 2026
                          <br />
                          <span class="text-secondary">Gudi Padwa</span>
                        </td>
                        <td class="text-secondary">Full Day</td>
                      </tr>
                      <tr>
                        <td>
                          1st May 2026
                          <br />
                          <span class="text-secondary">Maharashtra Day</span>
                        </td>
                        <td class="text-secondary">Full Day</td>
                      </tr>
                      {/* <tr>
                        <td>
                          14th May 2025
                          <br />
                          <span class="text-secondary">
                            Ch. Sambhaji Maharaj Jayanti
                          </span>
                        </td>
                        <td class="text-secondary">Half Day</td>
                      </tr> */}
                      <tr>
                        <td>
                          15th August 2026
                          <br />
                          <span class="text-secondary">Independence Day</span>
                        </td>
                        <td class="text-secondary">Full Day</td>
                      </tr>
                      <tr>
                        <td>
                          14th September 2026
                          <br />
                          <span class="text-secondary">Ganesh Chaturthi</span>
                        </td>
                        <td class="text-secondary">Full Day</td>
                      </tr>
                      <tr>
                        <td>
                          20th October 2026
                          <br />
                          <span class="text-secondary">
                            Vijayadashami (Dasara)
                          </span>
                        </td>
                        <td class="text-secondary">Full Day</td>
                      </tr>
                      <tr>
                        <td>
                          8th November 2026
                          <br />
                          <span class="text-secondary">
                            Diwali (laxmi pujan)
                          </span>
                        </td>
                        <td class="text-secondary">Full Day</td>
                      </tr>
                      <tr>
                        <td>
                          11th November 2026
                          <br />
                          <span class="text-secondary">Bhaubij</span>
                        </td>
                        <td class="text-secondary">Full Day</td>
                      </tr>
                      <tr>
                        <td>
                          25th December 2026
                          <br />
                          <span class="text-secondary">Christmas</span>
                        </td>
                        <td class="text-secondary">Full Day</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="col-md-8 col-sm-12">
              <div className="card">
                <Formik
                  initialValues={EmployeeLeavesInfo}
                  validationSchema={EmployeeLeaveNewSchema}
                  onSubmit={handleSubmit}
                >
                  {(props) => (
                    <Form>
                      <div className="card-body row">
                        <h4>
                          <span class="badge bg-purple-lt me-1">
                            Total leaves:{" "}
                            {employeeDetails?.employeementStatus ===
                            "activePosition"
                              ? 15
                              : 0}
                          </span>
                          <span class="badge bg-indigo-lt me-1">
                            Sick leaves taken: {leaveCount.paid}
                          </span>
                          <span class="badge bg-green-lt me-1">
                            Casual leaves taken: {leaveCount.unpaid}
                          </span>
                          <span class="badge bg-yellow-lt me-1">
                            Pending leaves:{" "}
                            {employeeDetails?.employeementStatus ===
                            "activePosition"
                              ? Math.max(
                                  15 - (leaveCount.paid + leaveCount.unpaid),
                                  0
                                )
                              : 0}
                          </span>
                        </h4>
                        <hr />
                        <div className="mb-3 row">
                          <label className="w-25 form-label required col-3">
                            Select Leave duration
                          </label>
                          <div className="w-100 col">
                            <div>
                              <label className="form-check form-check-inline">
                                <CustomRadioButton
                                  name="leaveType"
                                  value="half day"
                                />
                                <span className="form-check-label">
                                  Half day
                                </span>
                              </label>
                              <label className="form-check form-check-inline">
                                <CustomRadioButton
                                  name="leaveType"
                                  value="full day"
                                />
                                <span className="form-check-label">
                                  Full day
                                </span>
                              </label>
                              <label className="form-check form-check-inline">
                                <CustomRadioButton
                                  name="leaveType"
                                  value="multiple days"
                                />
                                <span className="form-check-label">
                                  Multiple days
                                </span>
                              </label>
                            </div>
                            <ErrorMessage
                              name="leaveType"
                              component="p"
                              className="text-danger"
                            />
                          </div>
                        </div>
                        {props.values.leaveType === "half day" && (
                          <div className="mb-3 row">
                            <label className="w-25 form-label required col-3">
                              Half day type
                            </label>
                            <div className="w-100 col">
                              <div>
                                <label className="form-check form-check-inline">
                                  <CustomRadioButton
                                    name="halfDayType"
                                    value="first half"
                                  />
                                  <span className="form-check-label">
                                    First half
                                  </span>
                                </label>
                                <label className="form-check form-check-inline">
                                  <CustomRadioButton
                                    name="halfDayType"
                                    value="second half"
                                  />
                                  <span className="form-check-label">
                                    Second half
                                  </span>
                                </label>
                              </div>
                              <ErrorMessage
                                name="halfDayType"
                                component="p"
                                className="text-danger"
                              />
                            </div>
                          </div>
                        )}
                        {props.values.leaveType !== "multiple days" && (
                          <div className="mb-3 row">
                            <label className="w-25 form-label required col-3">
                              Leave Date
                            </label>
                            <div className="w-100 col">
                              <CustomInput
                                name="leaveDate"
                                type="date"
                                min={new Date().toISOString().split("T")[0]}
                              />
                              <ErrorMessage
                                name="leaveDate"
                                component="p"
                                className="text-danger"
                              />
                            </div>
                          </div>
                        )}
                        {props.values.leaveType === "multiple days" && (
                          <>
                            <div className="mb-3 row">
                              <label className="w-25 form-label required col-3">
                                Start date
                              </label>
                              <div className="w-100 col">
                                <CustomInput
                                  name="leaveStartDate"
                                  type="date"
                                  min={new Date().toISOString().split("T")[0]}
                                />
                                <ErrorMessage
                                  name="leaveStartDate"
                                  component="p"
                                  className="text-danger"
                                />
                              </div>
                            </div>
                            <div className="mb-3 row">
                              <label className="w-25 form-label required col-3">
                                End date
                              </label>
                              <div className="w-100 col">
                                <CustomInput
                                  name="leaveEndDate"
                                  type="date"
                                  min={new Date().toISOString().split("T")[0]}
                                />
                                <ErrorMessage
                                  name="leaveEndDate"
                                  component="p"
                                  className="text-danger"
                                />
                              </div>
                            </div>
                          </>
                        )}

                        <div className="mb-3 row">
                          <label className="w-25 form-label required col-3">
                            Reason type
                          </label>
                          <div className="w-100 col">
                            <div>
                              <label className="form-check form-check-inline">
                                <CustomRadioButton
                                  name="leaveReasonType"
                                  value="sick"
                                />
                                <span className="form-check-label">Sick</span>
                              </label>
                              <label className="form-check form-check-inline">
                                <CustomRadioButton
                                  name="leaveReasonType"
                                  value="casual"
                                />
                                <span className="form-check-label">
                                  Casual / Comp off
                                </span>
                              </label>
                            </div>
                            <ErrorMessage
                              name="leaveReasonType"
                              component="p"
                              className="text-danger"
                            />
                          </div>
                        </div>

                        <div className="mb-3 row">
                          <label className="w-25 form-label required col-3">
                            Leave Reason
                          </label>
                          <div className="w-100 col">
                            <CustomTextArea name="leaveReason" type="text" />
                            <ErrorMessage
                              name="leaveReason"
                              component="p"
                              className="text-danger"
                            />
                          </div>
                        </div>
                        <div className="mb-3 row">
                          <label className="w-25 form-label col-3">
                            Supporting Document
                          </label>
                          <div className="w-100 col">
                            <CustomInput
                              name="supportingDocument"
                              type="file"
                              disabled={
                                props.values.leaveStatus === "approved" ||
                                props.values.leaveStatus === "rejected"
                              }
                              onChange={(e) =>
                                props.setFieldValue(
                                  "supportingDocument",
                                  e.target.files[0]
                                )
                              }
                            />
                            <ErrorMessage
                              name="supportingDocument"
                              component="p"
                              className="text-danger"
                            />
                            <p className="text-danger">
                              Submit the supporting document before your leave.
                              It must be submitted within 2 days after the leave
                              end date to avoid automatic rejection.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="card-footer">
                        <div className="btn-list justify-content-end">
                          <FormSubmitButton
                            buttonText="Add Leaves"
                            disabled={disableButton}
                          />
                          <SimpleButton buttonText="Cancel" type="secondary" />
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
              <div className="card p-3 my-2">
                <ul>
                  <li>
                    During the probation or notice period, all leaves will be
                    unpaid.
                  </li>

                  <li>
                    You can avail a maximum of 3 compensatory-off (comp-off)
                    leaves in a month.
                  </li>

                  <li className="text-orange">
                    For sick leave, you must submit a doctor’s prescription. For
                    Casual leave, you must submit a written leave document
                    signed and approved by two members of your higher authority.
                  </li>

                  <li>
                    All required documents must be submitted within 2 days after
                    the leave end date for the leave to be approved.
                  </li>

                  <li>
                    If the required documents are not submitted within the
                    specified time, the system will automatically reject the
                    leave request.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default AddEmployeeLeves;
